import { Text } from '@/components/ui/text';
import { useEffect, useState, type FC } from 'react';

interface Fda510kInputsProps {
  initialQuery: string;
  onQueryChange: (query: string, isValid: boolean) => void;
  showErrors: boolean;
  setShowErrors: (showErrors: boolean) => void;
}

export const Fda510kInputs: FC<Fda510kInputsProps> = ({
  initialQuery,
  onQueryChange,
  showErrors,
  setShowErrors,
}) => {
  const [productCode, setProductCode] = useState('');
  const [kNumber, setKNumber] = useState('');
  const [deviceName, setDeviceName] = useState('');
  const [applicantName, setApplicantName] = useState('');

  const errorMessage =
    'At least one search criterion is required (product code, K-number, device name, or applicant name)';

  // Parse initial query
  useEffect(() => {
    if (initialQuery) {
      const parts = initialQuery.split('|').map(p => p.trim());
      setProductCode(parts[0] || '');
      setKNumber(parts[1] || '');
      setDeviceName(parts[2] || '');
      setApplicantName(parts[3] || '');
    }
  }, [initialQuery]);

  // Build and notify query changes
  useEffect(() => {
    const buildQuery = () => {
      let query = productCode.trim();
      query += ` | ${kNumber.trim()}`;
      query += ` | ${deviceName.trim()}`;
      query += ` | ${applicantName.trim()}`;
      return query;
    };

    const query = buildQuery();
    // Query is valid if at least one field has a value
    const isValid =
      productCode.trim().length > 0 ||
      kNumber.trim().length > 0 ||
      deviceName.trim().length > 0 ||
      applicantName.trim().length > 0;
    onQueryChange(query, isValid);
    if (isValid) {
      setShowErrors(false);
    }
  }, [productCode, kNumber, deviceName, applicantName, onQueryChange]);

  return (
    <>
      {/* Product Code Input */}
      <div className="flex flex-col gap-1 w-full">
        <Text variant="body-sm" className="font-medium text-neutral-600">
          Product Code (optional)
        </Text>
        <input
          type="text"
          placeholder="e.g., NOF, DTB, or LWG"
          value={productCode}
          onChange={e => setProductCode(e.target.value.toUpperCase())}
          className="rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
        />
        {showErrors && (
          <Text variant="body-xs" color="danger">
            {errorMessage}
          </Text>
        )}
      </div>

      {/* K-number Input */}
      <div className="flex flex-col gap-1 w-full">
        <Text variant="body-sm" className="font-medium text-neutral-600">
          K-number (optional)
        </Text>
        <input
          type="text"
          placeholder="e.g., K123456"
          value={kNumber}
          onChange={e => setKNumber(e.target.value.toUpperCase())}
          className="rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
        />
        {showErrors && (
          <Text variant="body-xs" color="danger">
            {errorMessage}
          </Text>
        )}
      </div>

      {/* Device Name Input */}
      <div className="flex flex-col gap-1 w-full">
        <Text variant="body-sm" className="font-medium text-neutral-600">
          Device Name (optional)
        </Text>
        <input
          type="text"
          placeholder="e.g., stethoscope, ultrasound"
          value={deviceName}
          onChange={e => setDeviceName(e.target.value)}
          className="rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
        />
        {showErrors && (
          <Text variant="body-xs" color="danger">
            {errorMessage}
          </Text>
        )}
      </div>

      {/* Applicant Name Input */}
      <div className="flex flex-col gap-1 w-full">
        <Text variant="body-sm" className="font-medium text-neutral-600">
          Applicant Name (optional)
        </Text>
        <input
          type="text"
          placeholder="e.g., Medtronic, Abbott"
          value={applicantName}
          onChange={e => setApplicantName(e.target.value)}
          className="rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
        />
        {showErrors && (
          <Text variant="body-xs" color="danger">
            {errorMessage}
          </Text>
        )}
      </div>
    </>
  );
};
